import { createPerfilApiClient, getPerfilCredentials } from './perfilApiClient';

export default {
    /**
     * Obtiene el listado de listas de abonados disponibles para el perfil.
     * Ruta: GET /f/perfilV2/listas-abonados
     *
     * @returns {Promise<Array<{ id: number|string, nombre: string }>>}
     */
    async getListasAbonados() {
        try {
            const token = localStorage.getItem('token');
            const tid = localStorage.getItem('hash_teatro');
            const params = {
                api_key: token,
                ...(tid ? { tid } : {}),
            };
            const response = await createPerfilApiClient().get('/f/perfilV2/listas-abonados', { params });
            const data = response.data ?? {};
            const rawItems = data.data ?? data.listas ?? data.items ?? [];
            return Array.isArray(rawItems) ? rawItems : [];
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[abonos] Error al obtener listas de abonados:', error);
            }
            throw error;
        }
    },

    /**
     * Obtiene el listado paginado de abonados del perfil.
     * Devuelve la estructura de paginación del backend sin normalización.
     *
     * @param {{ page?: number, per_page?: number, search?: string, lista_id?: number|string }} params
     * @returns {Promise<{ items: Array, meta: Object }>}
     */
    async getAbonados({ page = 1, per_page = 10, search = '', lista_id = null } = {}) {
        try {
            const token = localStorage.getItem('token');
            const tid = localStorage.getItem('hash_teatro');
            const params = {
                page,
                per_page,
                api_key: token,
                ...(tid ? { tid } : {}),
            };
            if (search) params.search = search;
            if (lista_id) params.lista_id = lista_id;
            const response = await createPerfilApiClient().get('/f/perfilV2/abonados', { params });

            const data = response.data ?? {};
            const rawItems = data.data ?? data.abonados ?? data.items ?? [];
            const items = Array.isArray(rawItems) ? rawItems : [];

            const meta = {
                current_page: data.current_page ?? data.meta?.current_page ?? 1,
                last_page: data.last_page ?? data.meta?.last_page ?? 1,
                total: data.total ?? data.meta?.total ?? items.length,
                per_page: data.per_page ?? data.meta?.per_page ?? items.length,
            };

            return { items, meta };
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[abonos] Error al obtener abonados:', error);
            }
            throw error;
        }
    },

    /**
     * Obtiene el detalle completo de un abonado por su ID.
     * Devuelve los datos tal como los entrega el backend, sin normalización.
     *
     * @param {number|string} abonadoId
     * @returns {Promise<Object>} datos crudos del backend
     */
    async getAbonadoDetalle(abonadoId) {
        try {
            const token = localStorage.getItem('token');
            const response = await createPerfilApiClient().get(`/f/perfilV2/abonados/${abonadoId}`, {
                params: { api_key: token },
            });
            return response.data?.data ?? response.data?.abonado ?? response.data ?? {};
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[abonos] Error al obtener detalle del abonado:', error);
            }
            throw error;
        }
    },

    /**
     * Desmarca la renovación automática de un abonado.
     * Ruta: PATCH /f/perfil/abono/{id}/desmarcar-renovacion-automatica
     *
     * @param {number|string} abonadoId
     * @returns {Promise<void>}
     */
    async desmarcarRenovacionAutomatica(abonadoId) {
        try {
            const token = localStorage.getItem('token');
            await createPerfilApiClient().patch(
                `/f/perfil/abono/${abonadoId}/desmarcar-renovacion-automatica`,
                {},
                { params: { api_key: token } }
            );
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[abonos] Error al desmarcar renovación automática:', error);
            }
            throw error;
        }
    },

    /**
     * Obtiene los eventos disponibles para liberar asiento de un abonado.
     * Ruta: GET /f/perfil/abono/{abonadoId}/liberar-asientos/get-eventos
     *
     * @param {number|string} abonadoId
     * @returns {Promise<Array>}
     */
    async getEventosLiberarAsiento(abonadoId) {
        try {
            const token = localStorage.getItem('token');
            const response = await createPerfilApiClient().get(
                `/f/perfil/abono/${abonadoId}/liberar-asientos/get-eventos`,
                { params: { api_key: token } }
            );
            return response.data?.data ?? response.data ?? [];
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[abonos] Error al obtener eventos para liberar asiento:', error);
            }
            throw error;
        }
    },

    /**
     * Obtiene las sesiones disponibles para liberar asiento de un abonado en un evento.
     * Ruta: GET /f/perfil/abono/{abonadoId}/liberar-asientos/{eventoId}/get-sesiones/
     *
     * @param {number|string} abonadoId
     * @param {number|string} eventoId
     * @returns {Promise<Array>}
     */
    async getSesionesLiberarAsiento(abonadoId, eventoId) {
        try {
            const token = localStorage.getItem('token');
            const response = await createPerfilApiClient().get(
                `/f/perfil/abono/${abonadoId}/liberar-asientos/${eventoId}/get-sesiones/`,
                { params: { api_key: token } }
            );
            return response.data?.data ?? response.data ?? [];
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[abonos] Error al obtener sesiones para liberar asiento:', error);
            }
            throw error;
        }
    },

    /**
     * Libera el asiento de un abonado para una sesión específica.
     * Ruta: GET /f/perfil/abono/{abonadoId}/liberar-asientos/{eventoId}/{sesionId}
     *
     * Coincide con el endpoint legacy definido en api-routes.js:
     *   'front.perfil.abono.liberar-asientos'
     *   url: '/f/perfil/abono/%abonado_id%/liberar-asientos/%evento_id%/%sesion_id%'
     *   method: 'GET'
     *
     * @param {number|string} abonadoId
     * @param {number|string} eventoId
     * @param {number|string} sesionId
     * @returns {Promise<Object>}
     */
    async liberarAsiento(abonadoId, eventoId, sesionId) {
        try {
            const token = localStorage.getItem('token');
            const response = await createPerfilApiClient().get(
                `/f/perfil/abono/${abonadoId}/liberar-asientos/${eventoId}/${sesionId}`,
                { params: { api_key: token } }
            );
            return response.data;
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[abonos] Error al liberar asiento:', error);
            }
            throw error;
        }
    },

    /**
     * Procesa el pago de plazos seleccionados.
     *
     * @param {Object} domainPayload — payload de dominio sin credenciales
     * @returns {Promise<void>}
     */
    async procesarPagoPlazos(domainPayload) {
        try {
            const { token, hashTeatro } = getPerfilCredentials();
            const payload = {
                tid: hashTeatro,
                ...domainPayload,
            };

            await createPerfilApiClient().post('/f/perfil/paycomet/procesar-pago-plazos', payload, {
                params: {
                    ...(token ? { api_key: token } : {}),
                },
            });
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[abonos] Error al procesar pago de plazos:', error);
            }
            throw error;
        }
    },
};
